import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOrderNote } from '../../order';
import { SVGS } from '../../assets';
import {
  getOrderDetailsData,
  PoOrderShipping,
  useAppSelector,
} from '@zonofi/zono-money-store';

const toLabel = (key: string) =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

export const useCartOtherDetails = (location?: PoOrderShipping) => {
  const { orderNoteFromEditor: orderNote } = useOrderNote();
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [includePaymentLink, setIncludePaymentLink] = useState<boolean>(true);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const orderDetailsValue = useAppSelector(getOrderDetailsData);

  const handleTypeSelect = (type: 'percentage' | 'amount') => {
    setInputValues((prev) => ({
      ...prev,
      advancePaymentType: type,
      advancePayment: '',
    }));
    setDropdownOpen(false);
  };

  const handleDropdownToggle = () => {
    setDropdownOpen((prev) => !prev);
  };

  const togglePaymentLink = () => {
    setIncludePaymentLink((prev) => !prev);
  };

  useEffect(() => {
    if (location) {
      setInputValues((prev) => ({
        ...prev,
        location:
          location.latitude || location.longitude
            ? `${location.latitude || '-'}, ${location.longitude || '-'}`
            : '',
        address: location.streetLine1,
      }));
    }
  }, [location]);

  useEffect(() => {
    if (
      Object.values(orderDetailsValue?.data ?? {}).some(
        (value) => value !== '' && value !== '0' && value !== '0.00'
      )
    ) {
      setInputValues(orderDetailsValue.data);
      setIncludePaymentLink(orderDetailsValue.includePaymentLink);
    } else {
      setInputValues((prev) => ({ ...prev, advancePaymentType: 'amount' }));
      setIncludePaymentLink(false);
    }
  }, [orderDetailsValue]);

  const { noteData } = useMemo(() => {
    if (!orderNote?.properties) {
      return { noteData: [], requiredKeys: [] };
    }

    const data = Object.keys(orderNote.properties).map((key) => ({
      key,
      label: toLabel(key),
      required: orderNote.required?.includes(key) ?? false,
    }));

    return {
      noteData: data,
      requiredKeys: data
        .filter((item) => item.required)
        .map((item) => item.key),
    };
  }, [orderNote]);

  const otherDetailsCard = useMemo(
    () => [
      {
        key: 'details',
        title: 'Other Details',
        data: [
          { key: 'poNumber', label: 'PO Number', required: false },
          { key: 'address', label: 'Permanent Address', required: false },
          {
            key: 'location',
            label: 'Location',
            required: false,
            icon: SVGS.MapIcon,
          },
        ],
      },
      {
        key: 'note',
        title: 'Notes (Custom)',
        data: noteData,
      },
    ],
    [noteData]
  );

  const handleChange = useCallback(
    (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValues((prev) => ({ ...prev, [key]: e.target.value }));
    },
    []
  );

  const setAmountValue = useCallback((value: string) => {
    setInputValues((prev) => ({ ...prev, advancePayment: value }));
  }, []);

  const setTermsAndConditions = useCallback((value: string) => {
    setInputValues((prev) => ({ ...prev, termsAndConditions: value }));
  }, []);

  const setPaymentTermsAndConditions = useCallback((value: string) => {
    setInputValues((prev) => ({ ...prev, paymentTermsAndConditions: value }));
  }, []);

  return {
    otherDetailsCard,
    handleChange,
    inputValues,
    includePaymentLink,
    togglePaymentLink,
    handleTypeSelect,
    handleDropdownToggle,
    setAmountValue,
    dropdownOpen,
    setTermsAndConditions,
    setPaymentTermsAndConditions,
  };
};
